import "./ICDCoding.scss";

import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import Card from "components/Card/Card";
import KaboodleToaster from "components/common/KaboodleToaster";
import type { OptionType } from "components/inputs";
import { Select } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import Tooltip from "components/Tooltip/Tooltip";
import debounce from "lodash/debounce";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useGetICDCodesByInputQuery } from "services/codes-api";
import type { ICDCode } from "types/DataTypes";
import { useToken } from "utils/context";

import type { ICDObject } from "./icdAdapter";

type ICDOptionType = OptionType & {
  description?: string;
  code?: string;
};

type ICDCodingItemProps = {
  codeNumber: number;
  icdCodeName: string;
  data: ICDObject;
  onSave: (field: string | null, value: unknown) => Promise<void>;
  onRemove: (codeName: string) => void;
  showAcceptanceButtons?: boolean;
  isSaving?: boolean;
};

const MAX_ICD_SEARCH_RESULTS = 25;

const ICDCodingItem = ({
  codeNumber,
  icdCodeName,
  data,
  onSave,
  onRemove,
  showAcceptanceButtons = true,
  isSaving = false,
}: ICDCodingItemProps) => {
  const { accessToken } = useToken();
  const [codeSearchTerm, setCodeSearchTerm] = useState<string | null>(null);
  const [descSearchTerm, setDescSearchTerm] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [description, setDescription] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isAccepted, setIsAccepted] = useState<boolean>(false);
  const [isAcceptanceSaving, setIsAcceptanceSaving] = useState(false);

  useEffect(() => {
    if (isSaving) {
      return;
    }

    // extract the code number from icdCodeName (e.g., "icd_diag_code_1" -> 1)
    const codeIndex = parseInt(icdCodeName.split("_").pop() || "0");

    if (codeIndex && data?.icd_codes?.[codeIndex]) {
      const codeEntry = data.icd_codes[codeIndex];
      setCode(codeEntry.code);
      setDescription(codeEntry.verbose || "");
      setIsAccepted(codeEntry.accepted);
    } else {
      setCode(null);
      setDescription("");
      setIsAccepted(false);
    }
  }, [data, icdCodeName, isSaving]);

  const debouncedSetCodeSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setCodeSearchTerm(value);
      }, 500),
    [],
  );

  const debouncedSetDescSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setDescSearchTerm(value);
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetCodeSearchTerm.cancel();
      debouncedSetDescSearchTerm.cancel();
    };
  }, [debouncedSetCodeSearchTerm, debouncedSetDescSearchTerm]);

  const {
    control,
    reset,
    formState: { errors },
  } = useForm<{ code: string }>();

  const { data: codeResults, isFetching: isCodeFetching } =
    useGetICDCodesByInputQuery(
      {
        icd_indicator: data?.icd_indicator,
        code__istartswith: codeSearchTerm,
        limit: MAX_ICD_SEARCH_RESULTS,
      },
      {
        skip: !accessToken || !codeSearchTerm,
      },
    );

  const { data: descResults, isFetching: isDescFetching } =
    useGetICDCodesByInputQuery(
      {
        icd_indicator: data?.icd_indicator,
        description__icontains: descSearchTerm,
        limit: MAX_ICD_SEARCH_RESULTS,
      },
      {
        skip: !accessToken || !descSearchTerm,
      },
    );

  const codeOptions = useMemo(() => {
    if (!codeResults?.results) return [];

    const usedCodes = Object.entries(data?.icd_codes || {})
      .filter(([key]) => Number(key) !== codeNumber)
      .map(([, entry]) => entry.code);

    return codeResults.results
      .filter((result: ICDCode) => !usedCodes.includes(result.code))
      .map((result: ICDCode) => ({
        label: result.code,
        value: result.code,
        description: result.description,
      }));
  }, [codeResults, data?.icd_codes, codeNumber]);

  const descOptions = useMemo(() => {
    if (!descResults?.results) return [];

    const usedCodes = Object.entries(data?.icd_codes || {})
      .filter(([key]) => Number(key) !== codeNumber)
      .map(([, entry]) => entry.code);

    return descResults.results
      .filter((result: ICDCode) => !usedCodes.includes(result.code))
      .map((result: ICDCode) => ({
        label: result.description,
        value: result.code,
        description: result.description,
      }));
  }, [descResults, data?.icd_codes, codeNumber]);

  const currentCode = useMemo(() => {
    return code
      ? {
          label: code,
          value: code,
          description: description,
        }
      : null;
  }, [code, description]);

  const currentDesc = useMemo(() => {
    return description
      ? {
          label: description,
          value: code || "",
          description: description,
        }
      : null;
  }, [code, description]);

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    onRemove(icdCodeName);
    setShowDeleteModal(false);
  };

  // Update form values when code changes
  useEffect(() => {
    reset({ code: code || "" });
  }, [code, reset]);

  const handleAcceptanceChange = (accepted: boolean) => {
    void (async () => {
      const previous = isAccepted;
      setIsAccepted(accepted);
      setIsAcceptanceSaving(true);
      try {
        await onSave(`${icdCodeName}_accepted`, accepted);
        KaboodleToaster.success(
          `ICD Code ${code} marked as ${accepted ? "accepted" : "denied"}`,
        );
      } catch {
        setIsAccepted(previous);
      } finally {
        setIsAcceptanceSaving(false);
      }
    })();
  };

  return (
    <>
      <Card
        heading={
          <div className="flex jc-sb ai-c">
            <span>
              ICD Code {codeNumber}
              {code ? ` - ${code}` : ""}
            </span>
            <div className="flex jc-end ai-b">
              <Tooltip
                content="Format: Letter followed by 3-5 digits (e.g., A123 for ICD-10 or 12345 for ICD-9)"
                variant="dark"
                place="left"
              >
                <i className="material-icons info-icon">help_outline</i>
              </Tooltip>
              <Tooltip content="Delete ICD code" variant="dark">
                <ButtonIcon
                  kind="neutral"
                  size="small"
                  onClick={handleDeleteClick}
                >
                  delete
                </ButtonIcon>
              </Tooltip>
            </div>
          </div>
        }
        classNames="ICDCodingItem data-block"
      >
        <Controller
          control={control}
          name="code"
          render={({ field: { name } }) => (
            <Select
              label="ICD Code"
              name={name}
              errors={errors}
              widthVariant="full"
              placeholder="ICD code"
              noOptionsMessage={({ inputValue }) =>
                !inputValue
                  ? "Begin typing an ICD code..."
                  : isCodeFetching
                    ? "Searching..."
                    : "No matches found."
              }
              options={codeOptions}
              value={currentCode}
              isLoading={isCodeFetching}
              onInputChange={(value: string) =>
                debouncedSetCodeSearchTerm(value)
              }
              onChange={(o: ICDOptionType) => {
                void (async () => {
                  if (!o) return;

                  const previousCode = code;
                  const previousDescription = description;

                  setCode(o.value?.toString() || null);
                  setDescription(o.description || "");

                  try {
                    await onSave(icdCodeName, {
                      code: o.value || "",
                      description: o.description || "",
                    });
                  } catch {
                    setCode(previousCode);
                    setDescription(previousDescription);
                  }
                })();
              }}
              readOnly={isSaving}
            />
          )}
        />
        <Select
          label="Description"
          name={`${icdCodeName}_description`}
          errors={errors}
          widthVariant="full"
          placeholder="Search description"
          noOptionsMessage={({ inputValue }) =>
            !inputValue
              ? "Begin typing a description..."
              : isDescFetching
                ? "Searching..."
                : "No matches found."
          }
          options={descOptions}
          value={currentDesc}
          isLoading={isDescFetching}
          onInputChange={(value: string) => debouncedSetDescSearchTerm(value)}
          onChange={(o: ICDOptionType) => {
            void (async () => {
              if (!o) return;

              const previousCode = code;
              const previousDescription = description;

              setCode(o.value?.toString() || null);
              setDescription(o.description || "");

              try {
                await onSave(icdCodeName, {
                  code: o.value || "",
                  description: o.description || "",
                });
              } catch {
                setCode(previousCode);
                setDescription(previousDescription);
              }
            })();
          }}
          readOnly={isSaving}
        />
        {code && showAcceptanceButtons && (
          <div className="flex">
            <Button
              className={`acceptance-button ${isAccepted ? "active" : ""}`}
              onClick={() => handleAcceptanceChange(true)}
              disabled={isAcceptanceSaving}
            >
              Accepted
            </Button>
            <Button
              className={`acceptance-button ${!isAccepted ? "active" : ""}`}
              onClick={() => handleAcceptanceChange(false)}
              disabled={isAcceptanceSaving}
            >
              Denied
            </Button>
          </div>
        )}
      </Card>
      {showDeleteModal && (
        <KaboodleModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          footer={
            <div className="flex jc-c">
              <Button onClick={handleDeleteConfirm} className="delete">
                Confirm Delete
              </Button>
              <Button onClick={() => setShowDeleteModal(false)}>Cancel</Button>
            </div>
          }
        >
          <Card heading="Delete ICD Code">
            <p>Are you sure you want to delete this ICD code?</p>
          </Card>
        </KaboodleModal>
      )}
    </>
  );
};

export default ICDCodingItem;
